import {
  Award,
  Brain,
  CirclePlay,
  Download,
  GitBranch,
  GraduationCap,
  Lightbulb,
  Loader2,
  Map as MapIcon,
  RefreshCw,
  Shield,
  SlidersHorizontal,
  Swords,
  Target,
  TriangleAlert
} from "lucide-react";
import { useState } from "react";
import type { AnalysisResult, ModelLoadProgress, MoveAnalysis, MoveGrade, MoveReport, ReportNoteKind } from "../engine/types";

interface AnalysisPanelProps {
  result: AnalysisResult | null;
  loading: boolean;
  modelProgress?: ModelLoadProgress | null;
  canAnalyze: boolean;
  controlsLocked?: boolean;
  autoAnalyze: boolean;
  engineLabel: string;
  selectedContinuationIndex: number | null;
  altShadowActive?: boolean;
  report?: MoveReport | null;
  lastGrade?: MoveGrade | null;
  lastMoveLabel?: string | null;
  showTerritory: boolean;
  canShowTerritory: boolean;
  onAnalyze: () => void;
  onToggleAutoAnalyze: () => void;
  onToggleTerritory: () => void;
  onPlayMove: (move: MoveAnalysis) => void;
  onToggleContinuation: (index: number) => void;
  onToggleAlternative?: () => void;
}

function scoreLine(scoreLeadBlack: number): string {
  const abs = Math.abs(scoreLeadBlack);
  if (abs < 0.5) return "Roughly even on the board";
  return `${scoreLeadBlack > 0 ? "Black" : "White"} ahead by about ${abs.toFixed(1)} points`;
}

function percent(value: number | undefined): string | null {
  if (value === undefined || Number.isNaN(value)) {
    return null;
  }
  return `${Math.round(value * 1000) / 10}%`;
}

function formatMb(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(0)} MB`;
}

function noteIcon(kind: ReportNoteKind) {
  switch (kind) {
    case "reply":
      return <Swords size={14} />;
    case "alternative":
      return <Lightbulb size={14} />;
    case "territory":
      return <MapIcon size={14} />;
    case "group":
      return <Shield size={14} />;
  }
}

function ModelProgressBar({ progress }: { progress: ModelLoadProgress }) {
  const pct = progress.total > 0 ? Math.min(100, Math.round((progress.loaded / progress.total) * 100)) : null;
  const preparing = progress.phase === "prepare";
  const label = preparing
    ? "Preparing engine…"
    : `Downloading model… ${formatMb(progress.loaded)}${progress.total ? ` / ${formatMb(progress.total)}` : ""}`;
  // First run downloads a large net; it is then cached on-device for instant, offline reuse.
  const value = preparing ? 100 : pct;
  return (
    <div className="model-progress" role="status" aria-live="polite">
      <div className="model-progress-label">
        <Download size={15} />
        <span>{label}</span>
        {value !== null ? <span className="model-progress-pct">{value}%</span> : null}
      </div>
      <div className="model-progress-track">
        <div
          className={`model-progress-fill${value === null || preparing ? " indeterminate" : ""}`}
          style={value !== null ? { width: `${value}%` } : undefined}
        />
      </div>
    </div>
  );
}

export function AnalysisPanel({
  result,
  loading,
  modelProgress,
  canAnalyze,
  controlsLocked = false,
  autoAnalyze,
  engineLabel,
  selectedContinuationIndex,
  report,
  lastGrade,
  lastMoveLabel,
  showTerritory,
  canShowTerritory,
  onAnalyze,
  onToggleAutoAnalyze,
  onToggleTerritory,
  onPlayMove,
  onToggleContinuation,
  onToggleAlternative,
  altShadowActive
}: AnalysisPanelProps) {
  const [showDetail, setShowDetail] = useState(false);
  return (
    <aside className="analysis-panel">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Local analysis</p>
          <h2>Next moves</h2>
        </div>
        <div className="panel-actions">
          <button
            className={`auto-action icon-only ${showTerritory && canShowTerritory ? "active" : ""}`}
            type="button"
            onClick={onToggleTerritory}
            disabled={!canShowTerritory}
            aria-pressed={showTerritory && canShowTerritory}
            aria-label="Toggle estimated territory"
            title="Toggle estimated territory"
          >
            <MapIcon size={17} />
          </button>
          <button
            className={`auto-action ${autoAnalyze ? "active" : ""}`}
            type="button"
            onClick={onToggleAutoAnalyze}
            disabled={controlsLocked}
            aria-pressed={autoAnalyze}
            title={controlsLocked ? "Analysis controls are hidden during Play vs AI" : "Auto analyze after each move"}
          >
            <RefreshCw size={17} />
            Auto
          </button>
          <button className="primary-action" type="button" onClick={onAnalyze} disabled={!canAnalyze || loading}>
            {loading ? <Loader2 className="spin" size={18} /> : <Brain size={18} />}
            Analyze
          </button>
        </div>
      </div>

      <div className="engine-line">
        <Target size={15} />
        <span>{engineLabel}</span>
      </div>

      {report ? (
        <section
          className={`grade-card report-card${report.grade ? ` grade-${report.grade.label.toLowerCase()}` : ""}`}
          aria-label="Move report"
        >
          <div className="grade-header">
            <Award size={17} />
            <span>
              Your <strong>{report.moveLabel}</strong>
              {report.grade ? ` — ${report.grade.label}` : ""}
            </span>
            {report.grade && report.grade.pointLoss >= 0.5 ? (
              <span className="grade-cost">−{report.grade.pointLoss.toFixed(1)} pts</span>
            ) : null}
          </div>
          <p className="report-verdict">{report.verdict}</p>
          {report.notes.length > 0 ? (
            <ul className="report-notes">
              {report.notes.map((note) => (
                <li key={note.kind}>
                  <span className={`report-note-icon ${note.kind}`}>{noteIcon(note.kind)}</span>
                  <span className="report-note-text">
                    {note.text}
                    {note.kind === "reply" && report.refutationIndex !== undefined ? (
                      <button
                        type="button"
                        className={`report-line-button${selectedContinuationIndex === report.refutationIndex ? " active" : ""}`}
                        onClick={() => onToggleContinuation(report.refutationIndex!)}
                        aria-pressed={selectedContinuationIndex === report.refutationIndex}
                        title="Preview this line on the board"
                      >
                        <GitBranch size={13} />
                        Show on board
                      </button>
                    ) : null}
                    {note.kind === "alternative" && report.alternativePreview && onToggleAlternative ? (
                      <button
                        type="button"
                        className={`report-line-button${altShadowActive ? " active" : ""}`}
                        onClick={onToggleAlternative}
                        aria-pressed={Boolean(altShadowActive)}
                        title={`Preview ${report.alternativePreview.label}'s line on the board`}
                      >
                        <GitBranch size={13} />
                        Show on board
                      </button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : lastGrade && lastMoveLabel ? (
        <section className={`grade-card grade-${lastGrade.label.toLowerCase()}`} aria-label="Move grade">
          <div className="grade-header">
            <Award size={17} />
            <span>
              Your <strong>{lastMoveLabel}</strong> — {lastGrade.label}
            </span>
            {lastGrade.pointLoss >= 0.5 ? <span className="grade-cost">−{lastGrade.pointLoss.toFixed(1)} pts</span> : null}
          </div>
          <p>
            {lastGrade.label === "Great" || lastGrade.bestLabel === lastMoveLabel ? (
              "That matches the engine's top choice."
            ) : (
              <>
                The engine preferred <strong>{lastGrade.bestLabel}</strong>
                {lastGrade.bestPv && lastGrade.bestPv.length > 0 ? `. Line: ${lastGrade.bestPv.slice(0, 6).join(" ")}` : ""}.
              </>
            )}
          </p>
        </section>
      ) : null}

      {loading && modelProgress ? <ModelProgressBar progress={modelProgress} /> : null}

      {result?.notice ? (
        <div className="notice">
          <TriangleAlert size={16} />
          <span>{result.notice}</span>
        </div>
      ) : null}

      {result ? (
        <>
          <p className="analysis-summary">{result.summary}</p>
          {result.rootScoreLead !== undefined ? <p className="score-readout">{scoreLine(result.rootScoreLead)}</p> : null}
          {result.lesson ? (
            <section className="lesson-card" aria-label="Coach lesson">
              <div className="lesson-header">
                <GraduationCap size={18} />
                <h3>{result.lesson.title}</h3>
              </div>
              <div className="lesson-rows">
                <p>
                  <span>Focus</span>
                  {result.lesson.focus}
                </p>
                <p>
                  <span>Try</span>
                  {result.lesson.tryThis}
                </p>
                <p>
                  <span>Compare</span>
                  {result.lesson.compare}
                </p>
                {result.lesson.continuation ? (
                  <p>
                    <span>Read</span>
                    {result.lesson.continuation}
                  </p>
                ) : null}
              </div>
            </section>
          ) : null}
          <div className="detail-toggle">
            <button
              type="button"
              className={showDetail ? "active" : ""}
              onClick={() => setShowDetail((shown) => !shown)}
              aria-pressed={showDetail}
              title="Show raw engine numbers"
            >
              <SlidersHorizontal size={14} />
              {showDetail ? "Coaching view" : "Engine detail"}
            </button>
          </div>
          <div className="move-list">
            {result.moves.map((move, index) => {
              const displayReasons = (showDetail ? move.reasons : move.reasons.filter((reason) => reason.category !== "engine")).slice(0, 4);
              const moveWinrate = percent(move.winrate);
              return (
                <article className="move-card" key={`${move.label}-${index}`}>
                  <div className="move-card-top">
                    <div className="rank-badge">{index + 1}</div>
                    <div className="move-main">
                      <h3>{move.label}</h3>
                      {showDetail ? (
                        <p>
                          {move.prior !== undefined ? `Policy ${percent(move.prior)}` : `Score ${move.score}`}
                          {move.visits ? ` · ${move.visits} visits` : ""}
                          {move.scoreLead !== undefined ? ` · Lead ${move.scoreLead.toFixed(1)}` : ""}
                        </p>
                      ) : null}
                    </div>
                    {moveWinrate ? (
                      <span className="move-winrate" title={`Win rate ${moveWinrate}`}>
                        Win {moveWinrate}
                      </span>
                    ) : null}
                    <div className="move-actions">
                      {move.pv && move.pv.length > 0 ? (
                        <button
                          className={`icon-text-button ${selectedContinuationIndex === index ? "active" : ""}`}
                          type="button"
                          onClick={() => onToggleContinuation(index)}
                          aria-pressed={selectedContinuationIndex === index}
                          title={`Shadow ${move.label} main continuation`}
                        >
                          <GitBranch size={17} />
                          PV
                        </button>
                      ) : null}
                      {move.kind === "move" ? (
                        <button className="icon-text-button" type="button" onClick={() => onPlayMove(move)} title={`Play ${move.label}`}>
                          <CirclePlay size={17} />
                          Play
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <ul className="reason-list">
                    {displayReasons.map((reason, reasonIndex) => (
                      <li key={`${move.label}-${reason.label}-${reason.impact}-${reasonIndex}`}>
                        <span className={`reason-tag ${reason.category}`}>{reason.category}</span>
                        <span>
                          <strong>{reason.label}.</strong> {reason.detail}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {showDetail && move.pv && move.pv.length > 0 ? <p className="pv-line">PV: {move.pv.slice(0, 8).join(" ")}</p> : null}
                </article>
              );
            })}
          </div>
        </>
      ) : (
        <div className="empty-analysis">
          <Brain size={28} />
          <p>Analyze the current board to rank candidate moves and show coach-style reasons.</p>
        </div>
      )}
    </aside>
  );
}
